import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Pencil, Trash2, ChevronRight } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import { formatCents, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ESTIMATE_STATUS_BADGE } from '@/lib/badges';
import { LinkPoToEstimateModal } from '@/components/LinkPoToEstimateModal';
import type { EstimateStatus } from '@billflow/shared';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface EstimateRow {
  id: number;
  number: string;
  customer_id: number;
  customer_name: string;
  date: string;
  status: EstimateStatus;
  total: number;
  amount_paid: number;
  linked_po_count: number;
  first_linked_po_id: number | null;
  first_linked_po_number: string | null;
  created_at: string;
}

// ─── Module-level constants ─────────────────────────────────────────────────

type StatusFilter = 'all' | EstimateStatus;

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Issued', value: 'issued' },
  { label: 'Partial', value: 'partial' },
  { label: 'Paid', value: 'paid' },
  { label: 'Returned', value: 'returned' },
];

/** Statuses where a meaningful balance is owed */
const BALANCE_STATUSES: EstimateStatus[] = ['issued', 'partial'];

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteDialogProps {
  estimate: EstimateRow;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteDialog({ estimate, onCancel, onConfirm, loading }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Estimate</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete estimate <span className="font-semibold text-foreground">{estimate.number}</span>
            ? This restores stock and removes payments. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function EstimatesPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<StatusFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [deletingEstimate, setDeletingEstimate] = useState<EstimateRow | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [linkPoFor, setLinkPoFor] = useState<EstimateRow | null>(null);

  const {
    rows: estimates,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
  } = usePaginatedList<EstimateRow>({
    endpoint: '/api/estimates',
    entityKey: 'estimates',
    defaultSort: 'date',
    defaultDir: 'desc',
    extraParams: {
      status: status !== 'all' ? status : undefined,
      from: from || undefined,
      to: to || undefined,
    },
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['estimates'] });
  }

  async function handleDelete() {
    if (!deletingEstimate) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/estimates/${deletingEstimate.id}`, { method: 'DELETE' });
      toast.success('Estimate deleted');
      setDeletingEstimate(undefined);
      refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete estimate';
      toast.error(msg);
      setDeletingEstimate(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Estimates</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* New Estimate now lives in the always-visible top bar (AppShell). */}
          <ExportButtons
            endpoint="/api/estimates"
            params={{
              status: status !== 'all' ? status : undefined,
              q: q || undefined,
              from: from || undefined,
              to: to || undefined,
            }}
          />
        </div>
      </div>

      {/* Filters — one line: search · date range · status pills (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search number or customer…"
            className="pl-9 h-9"
          />
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-muted-foreground">From</span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          <span className="text-muted-foreground">To</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* Status pills */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 shrink-0">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Number"
                sortKey="number"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-32"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Customer</th>
              <SortableTh
                label="Date"
                sortKey="date"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="hidden sm:table-cell w-28"
              />
              <SortableTh
                label="Total"
                sortKey="total"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <SortableTh
                label="Paid"
                sortKey="paid"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28 hidden md:table-cell"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground w-28 hidden md:table-cell">
                Balance
              </th>
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-24"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-28 hidden md:table-cell">
                Linked PO
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && estimates.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                  No estimates found.
                </td>
              </tr>
            )}
            {estimates.map((est) => {
              const badge = ESTIMATE_STATUS_BADGE[est.status];
              const showBalance = BALANCE_STATUSES.includes(est.status);
              const balance = est.total - est.amount_paid;
              return (
                <tr key={est.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/estimates/${est.id}`}
                      className="font-mono text-xs hover:underline underline-offset-4"
                    >
                      {est.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/customers/${est.customer_id}`}
                      className="hover:underline underline-offset-4 text-muted-foreground"
                    >
                      {est.customer_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell whitespace-nowrap">
                    {formatDate(est.date)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCents(est.total)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground hidden md:table-cell">
                    {formatCents(est.amount_paid)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">
                    {showBalance ? (
                      <span className={balance > 0 ? 'text-amber-700 font-medium' : ''}>
                        {formatCents(balance)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                    {est.linked_po_count > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        <Link
                          to={`/purchase-orders/${est.first_linked_po_id}`}
                          className="font-mono text-xs hover:underline underline-offset-4"
                        >
                          {est.first_linked_po_number}
                        </Link>
                        {est.linked_po_count > 1 && (
                          <Link
                            to={`/estimates/${est.id}`}
                            className="text-muted-foreground text-xs hover:underline underline-offset-4"
                          >
                            +{est.linked_po_count - 1}
                          </Link>
                        )}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setLinkPoFor(est)}
                        className="text-muted-foreground text-xs hover:text-primary transition-colors"
                      >
                        Link
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link to={`/estimates/${est.id}`}>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                          View
                          <ChevronRight className="h-3 w-3 ml-0.5" />
                        </Button>
                      </Link>
                      {(est.status === 'draft' || (isAdmin && est.status === 'issued')) && (
                        <Link to={`/estimates/${est.id}/edit`}>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </Link>
                      )}
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                          onClick={() => setDeletingEstimate(est)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Delete dialog */}
      {deletingEstimate && (
        <DeleteDialog
          estimate={deletingEstimate}
          onCancel={() => setDeletingEstimate(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}

      {/* Link PO modal */}
      {linkPoFor && (
        <LinkPoToEstimateModal
          estimateId={linkPoFor.id}
          estimateNumber={linkPoFor.number}
          onClose={() => setLinkPoFor(null)}
          onLinked={() => setLinkPoFor(null)}
        />
      )}
    </div>
  );
}
